import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApiData } from "../../contexts/ApiData";
import { useTranslations } from "../../contexts/Translation";
import { useOrganizations } from "../../contexts/UserProvider";
import { Group, ImprovementGoal } from "../../types";
import AiCoach from "../AiCoach/AiCoach";
import styles from "./Details.module.css";

// Fixed map from value to key, so an unknown value never becomes a key path on screen
const accessLabels: Record<string, string> = {
  me: "accessibleByMe",
  supporters: "accessibleBySupporters",
  org: "accessibleByOrganization",
};
const accessLabel = (accessibleBy: string) => accessLabels[accessibleBy] ?? accessLabels.me;

export const NextSurveyCard = () => {
  const t = useTranslations("improvementGoals");
  return (
    <section className={styles.sideCard} aria-labelledby='next-survey-heading'>
      <h2 id='next-survey-heading' className={styles.sideHeading}>
        {t("nextSurveyHeading")}
      </h2>
      <p className={styles.text}>{t("nextSurveyText")}</p>
      <Link className={styles.button} to='/survey'>
        {t("createSurvey")}
      </Link>
    </section>
  );
};

export const SuccessCard = () => {
  const t = useTranslations("improvementGoals");
  return (
    <section className={styles.sideCard} aria-labelledby='success-heading'>
      <h2 id='success-heading' className={styles.sideHeading}>
        {t("successHeading")}
      </h2>
      <p className={styles.text}>{t("successText1")}</p>
      <p className={styles.text}>{t("successText2")}</p>
      <p className={styles.text}>{t("successText3")}</p>
    </section>
  );
};

// Lisa's card in the side panel. The coach button is only for the owner of the goal.
export const CoachCard = ({ isOwner, coach }: { isOwner: boolean; coach: ReactNode }) => {
  const t = useTranslations("improvementGoals");
  return (
    <section className={[styles.sideCard, styles.dark].join(" ")} aria-labelledby='goal-coach-heading'>
      <h2 id='goal-coach-heading' className={styles.sideHeading}>
        {t("coachHeading")}
      </h2>
      <div className={styles.coach}>
        <img src='/assets/coach-lisa.jpg' alt='' width={48} height={48} />
        <span>{t("coachName")}</span>
      </div>
      <p className={styles.text}>{t("coachText")}</p>
      {isOwner && coach}
    </section>
  );
};

type GoalAsideProps = {
  goal: ImprovementGoal;
  isOwner: boolean;
  onCoachComment: () => void;
};

const GoalAside = ({ goal, isOwner, onCoachComment }: GoalAsideProps) => {
  const t = useTranslations("improvementGoals");
  const tSurvey = useTranslations("surveyForm");
  const { get } = useApiData();
  const { currentOrganization } = useOrganizations();
  const [groups, setGroups] = useState<Group[]>([]);
  // Only the owner gets the group ids; a shared user sees the label without names
  const groupIds = goal.groupsWithAccess || [];
  const hasGroups = groupIds.length > 0;
  const isSelectedGroups = goal.accessibleBy === "selectedGroups";

  useEffect(() => {
    if (!hasGroups || !currentOrganization) {
      return;
    }
    const call = async () => {
      const result = await get(`/organization/${currentOrganization}/groups`);
      setGroups(result || []);
    };
    call();
  }, [get, hasGroups, currentOrganization]);

  const groupNames = groupIds.map((id) => groups.find((g) => g.id === id)?.name).filter(Boolean);
  const access = goal.accessibleBy || "me";

  return (
    <>
      <CoachCard
        isOwner={isOwner}
        coach={<AiCoach coachingType='goalComment' goalId={goal.id} variant='onDark' buttonLabel={t("askCoach")} onComplete={onCoachComment} />}
      />
      <NextSurveyCard />
      <SuccessCard />
      <section className={styles.sideCard} aria-labelledby='shared-heading'>
        <h2 id='shared-heading' className={styles.sideHeading}>
          {t("sharedWithHeading")}
        </h2>
        <p className={styles.text}>
          <strong>{isSelectedGroups ? tSurvey("accessibleBySelectedGroups") : t(accessLabel(access))}</strong>
        </p>
        {groupNames.length > 0 && <p className={styles.text}>{groupNames.join(", ")}</p>}
        {goal.sharedBy && <p className={styles.text}>{t("sharedBy", goal.sharedBy)}</p>}
      </section>
    </>
  );
};

export default GoalAside;
